/**
 * /direccion/dashboard · Dashboard ejecutivo Dirección General (Fase 18.7)
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
    LineChart as LC, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import {
    LineChart, Zap, FileDown, AlertTriangle,
    TrendingUp, Shield, Activity, RefreshCw,
} from "lucide-react";
import { fmtMXN, fmtFecha } from "../regulatorios/_common";

const DONUT_COLORS = ["#C08A2E", "#0E1A2B", "#E8663D", "#8a8a8a", "#2b4b7a", "#6a7a8a"];

export default function DashboardDG() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/direccion/dashboard");
            setData(r.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const dossier = () => window.open(
        `${api.defaults.baseURL}/direccion/dossier/pdf`, "_blank",
    );

    if (loading && !data) return <div className="p-6 text-muted-foreground">Cargando dashboard…</div>;
    if (!data) return null;

    const k = data.kpis || {};
    const al = data.alertas_hub || {};

    const tiles = [
        { label: "Fideicomisos activos", valor: k.fideicomisos_activos, icon: Shield },
        { label: "Patrimonio administrado", valor: fmtMXN(k.patrimonio_mxn_total), icon: TrendingUp },
        { label: "Honorarios mes", valor: fmtMXN(k.honorarios_mes_mxn), icon: Activity },
        { label: "Dictámenes F-09 vigentes", valor: k.dictamenes_f09_vigentes, icon: Shield },
        { label: "Retenciones LISR mes", valor: fmtMXN(k.retenciones_lisr_mes_mxn), icon: Activity },
        { label: "Estados cuenta pendientes", valor: k.estados_cuenta_pendientes, icon: AlertTriangle },
        { label: "Alertas rojas", valor: al.total_rojo, cls: "border-red-300", icon: AlertTriangle },
        { label: "Alertas ámbar", valor: al.total_ambar, cls: "border-amber-300", icon: AlertTriangle },
    ];

    return (
        <div className="space-y-6" data-testid="dashboard-dg">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Dirección General · Dossier Ejecutivo
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Panorama agregado — tenant actual · Generado {fmtFecha(data.generado_at)}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={load} data-testid="dg-refresh">
                        <RefreshCw className="h-4 w-4 mr-1" /> Refrescar
                    </Button>
                    <Button
                        className="bg-[color:var(--brand-dark-gold)] hover:bg-[color:var(--brand-dark-gold)]/90 text-white"
                        size="sm"
                        onClick={dossier}
                        data-testid="dg-dossier-pdf"
                    >
                        <FileDown className="h-4 w-4 mr-1" /> Descargar dossier PDF
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {tiles.map((t, i) => (
                    <Card key={i} className={t.cls || ""} data-testid={`dg-tile-${i}`}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">{t.label}</span>
                                <t.icon className="h-4 w-4 opacity-50" />
                            </div>
                            <p className="text-2xl font-semibold mt-1">{t.valor ?? "—"}</p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                    <CardHeader><CardTitle className="text-base">Patrimonio últimos 6 meses</CardTitle></CardHeader>
                    <CardContent className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.series?.patrimonio_6m || []}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis dataKey="periodo" fontSize={10} />
                                <YAxis fontSize={10} tickFormatter={(v) =>
                                    v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M`
                                        : v >= 1_000 ? `${(v / 1_000).toFixed(0)}K` : v
                                } />
                                <Tooltip formatter={(v) => fmtMXN(v)} />
                                <Bar dataKey="patrimonio" fill="#C08A2E" radius={4} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle className="text-base">Fideicomisos por producto</CardTitle></CardHeader>
                    <CardContent className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={data.series?.fideicomisos_por_producto || []}
                                    dataKey="fideicomisos" nameKey="producto"
                                    innerRadius={40} outerRadius={75}
                                    label={(e) => `${e.producto} (${e.fideicomisos})`}
                                >
                                    {(data.series?.fideicomisos_por_producto || []).map((_, i) =>
                                        <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Shield className="h-4 w-4" /> Business Continuity (BCP)
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                    <p>
                        <b>Último BCP cerrado:</b>{" "}
                        {k.ultimo_bcp_folio ? (
                            <>
                                <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 border mr-2">
                                    {k.ultimo_bcp_folio}
                                </Badge>
                                <span className="text-muted-foreground">
                                    {fmtFecha(k.ultimo_bcp_fecha)}
                                </span>
                            </>
                        ) : <span className="text-amber-700">Sin pruebas BCP registradas</span>}
                    </p>
                    <p>
                        <b>Próximo BCP planeado:</b>{" "}
                        {k.proximo_bcp_folio ? (
                            <>
                                <Badge className="bg-sky-100 text-sky-900 border-sky-300 border mr-2">
                                    {k.proximo_bcp_folio}
                                </Badge>
                                <span className="text-muted-foreground">
                                    {fmtFecha(k.proximo_bcp_fecha)}
                                </span>
                            </>
                        ) : <span className="text-amber-700">Sin BCP programado</span>}
                    </p>
                </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground">
                Dossier generado automáticamente para uso interno DG / Consejo · Hash SHA-256 al descargar.
            </p>
        </div>
    );
}
